import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ShieldCheck, UserRound } from "lucide-react";
import { api, formatError } from "@/lib/api";
import ForgotPasswordLink from "@/components/ForgotPasswordLink";
import { useAuth } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function StaffLogin({ mode = "staff" }) {
  const { login } = useAuth();
  const nav = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const isAdmin = mode === "admin";
  const Icon = isAdmin ? ShieldCheck : UserRound;
  const title = isAdmin ? "Admin Login" : "Staff Login";

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/staff/login", {
        username: username.trim(),
        password,
      });
      if (isAdmin && data.user.role !== "admin") {
        toast.error("This account does not have admin access.");
        setLoading(false);
        return;
      }
      login(data.access_token, { ...data.user });
      toast.success(`Welcome, ${data.user.name}`);
      if (data.user.role === "admin") nav("/admin");
      else nav("/staff");
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2" style={{ background: "var(--bg)" }}>
      <div className="hidden md:block landing-hero" />
      <div className="p-8 sm:p-12 flex flex-col">
        <Link
          to="/"
          data-testid="back-to-landing"
          className="inline-flex items-center gap-2 text-sm"
          style={{ color: "var(--text-muted)" }}
        >
          <ArrowLeft className="h-4 w-4" /> Back
        </Link>

        <div className="my-auto max-w-md w-full">
          <div className="flex items-center gap-3 mb-8">
            <div className="h-10 w-10 rounded-xl bg-navy text-white grid place-items-center">
              <Icon className="h-5 w-5" />
            </div>
            <div>
              <div className="label-caps gold">
                {isAdmin ? "Administrator" : "Front Desk"}
              </div>
              <h1 className="text-3xl font-bold leading-tight"
                  style={{ letterSpacing: "-0.02em" }}>
                {title}
              </h1>
            </div>
          </div>

          <form onSubmit={submit} className="space-y-5" data-testid={`${mode}-login-form`}>
            <div className="space-y-1.5">
              <Label htmlFor="user">Username</Label>
              <Input
                id="user"
                data-testid={`${mode}-username-input`}
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pwd">Password</Label>
              <Input
                id="pwd"
                data-testid={`${mode}-password-input`}
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid={`${mode}-login-submit`}
              className="w-full h-11 bg-navy hover:opacity-90 text-white rounded-xl"
            >
              {loading ? "Signing in…" : "Sign in"}
            </Button>
            <div className="text-center">
              <ForgotPasswordLink role="staff" />
            </div>

            <div className="mt-8 rounded-xl p-3 text-xs space-y-1"
                 style={{ background: "var(--bg-soft)", color: "var(--text-muted)" }}>
              <div>
                <span className="font-medium" style={{ color: "var(--text)" }}>
                  Demo {isAdmin ? "admin" : "staff"}:
                </span>{" "}
                <span className="font-mono">
                  {isAdmin ? "admin / admin123" : "staff / staff123"}
                </span>
              </div>
              <div className="text-amber-700 dark:text-amber-400">
                Change these passwords before real use.
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
